<template>
    <TriadModal ref="modal" title="Edit Tags">
        <div class="flex flex-col gap-4">
            <div class="flex flex-wrap gap-2 border border-gray-300 rounded-lg p-3 bg-white focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-transparent transition-all" @click="focusInput">
                <div ref="tagsContainer" class="flex flex-wrap gap-2 items-center">
                    <span v-for="(tag, index) in localTags" 
                        :key="typeof tag === 'object' && tag !== null ? tag.name : tag" 
                        class="px-2 py-1 rounded-full text-sm font-medium flex items-center gap-1 transition-colors cursor-move tag-item"
                        :style="getTagStyle(tag)">
                        {{ getTagName(tag) }}
                        <button @click.stop="removeTag(index)" 
                            class="ml-1 hover:text-red-700 focus:outline-none rounded-full p-0.5 hover:bg-black/10 transition-colors">
                            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                        </button>
                    </span>
                </div>
                
                <input 
                    ref="tagInput"
                    v-model="currentInput" 
                    @keydown.enter.prevent="addTagFromInput" 
                    @keydown.backspace="handleBackspace"
                    @blur="addTagFromInput"
                    type="text"
                    placeholder="Add a tag..." 
                    class="outline-none flex-grow min-w-[120px] bg-transparent text-gray-700 placeholder-gray-400 border-none focus:ring-0 p-0"
                />
                
                <!-- AI Button -->
                <AIGenerateButton 
                  v-if="aiContext" 
                  :loading="isGeneratingTags" 
                  @click.stop="generateTagsAI" 
                  customClass="ml-auto self-center"
                />
            </div>
            
            <div class="flex justify-end gap-3 mt-4">
                <Button @click="cancel" type="secondary" class="!ms-0">
                    Cancel
                </Button>
                <Button @click="confirm" class="!ms-0">
                    Save Changes
                </Button>
            </div>
        </div>
    </TriadModal>
</template>

<script setup>
import { ref, defineExpose, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import TriadModal from '@/components/common/TriadModal.vue';
import { generateTagColors, getLocalizedTagName } from '@/service/tagColor.js';
import Button from '@/components/form/TriadButton.vue';
import AIGenerateButton from '@/components/common/elements/AIGenerateButton.vue';
import toastStore from '@/store/toast.store';
import Sortable from 'sortablejs';

const { locale } = useI18n();

const getTagName = (tag) => {
    return getLocalizedTagName(tag, locale.value);
};

const modal = ref(null);
const localTags = ref([]);
const currentInput = ref('');
const tagInput = ref(null);
const tagsContainer = ref(null);
const isGeneratingTags = ref(false);
const aiContext = ref(null);

onMounted(() => {
    if (tagsContainer.value) {
        Sortable.create(tagsContainer.value, {
            animation: 150,
            draggable: '.tag-item',
            onEnd: (evt) => {
                const item = localTags.value.splice(evt.oldIndex, 1)[0];
                localTags.value.splice(evt.newIndex, 0, item);
            }
        });
    }
});

let resolvePromise = null;

const open = (tags, context = null) => {
    // Deep copy tags to avoid modifying original until save
    const parsedTags = JSON.parse(JSON.stringify(tags || []));
    localTags.value = parsedTags.filter(tag => {
        const name = typeof tag === 'object' && tag !== null ? tag.name : tag;
        return name && name.toLowerCase() !== 'unknown';
    });
    currentInput.value = '';
    aiContext.value = context;
    modal.value.open();
    
    // Return a promise that resolves when saved
    return new Promise((resolve) => {
        resolvePromise = resolve;
    });
};

const close = () => {
    modal.value.close();
    resolvePromise = null;
};

const cancel = () => {
    close();
};

const confirm = () => {
    if (resolvePromise) {
        resolvePromise(localTags.value);
    }
    close();
};

const focusInput = () => {
    if (tagInput.value) {
        tagInput.value.focus();
    }
};

const getTagStyle = (tag) => {
    if (typeof tag === 'object' && tag !== null) {
        const bg = tag.color_background;
        const fg = tag.color_font;
        if (bg && fg) {
            return { backgroundColor: bg, color: fg };
        }
    }
    const name = typeof tag === 'object' && tag !== null ? tag.name : tag;
    if (name) {
        const c = generateTagColors(name);
        return { backgroundColor: c.color_background, color: c.color_font };
    }
    return { backgroundColor: '#f3f4f6', color: '#374151' };
};

const addTagFromInput = () => {
    const name = currentInput.value.trim();
    if (!name) return;

    // Check for duplicates
    const currentTagNames = localTags.value.map(t => (typeof t === 'object' && t !== null ? t.name : t) || '');
    if (!currentTagNames.some(tName => tName.toLowerCase() === name.toLowerCase())) {
        localTags.value.push({ name: name });
    }
    currentInput.value = '';
};

const handleBackspace = () => {
    if (currentInput.value === '' && localTags.value.length > 0) {
        // Remove the last tag if input is empty and backspace is pressed
        localTags.value.pop();
    }
};

const removeTag = (index) => {
    localTags.value.splice(index, 1);
};

const generateTagsAI = async () => {
  if (!aiContext.value) return;
  
  isGeneratingTags.value = true;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 60000);

  try {
    const response = await fetch('/api/ai/generate-tags', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(aiContext.value),
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);

    if (response.ok) {
      const data = await response.json();
      if (data.tags && Array.isArray(data.tags)) {
        // add avoiding duplicates
        const currentTagNames = localTags.value.map(t => (typeof t === 'object' && t !== null ? t.name : t) || '');
        const newTags = data.tags.filter(t => {
            const tName = typeof t === 'object' && t !== null ? t.name : t;
            return tName && !currentTagNames.some(existing => existing.toLowerCase() === tName.toLowerCase());
        });
        
        localTags.value = [...localTags.value, ...newTags];
        
        toastStore.dispatch('addMessage', {
          message: 'Tags generated successfully!',
          icon: 'success',
          ttl: 3000
        });
      }
    } else {
      throw new Error('Failed to generate tags');
    }
  } catch (error) {
    if (error.name === 'AbortError') {
      toastStore.dispatch('addMessage', {
        message: 'Tag generation timed out after 1 minute.',
        icon: 'warning',
        ttl: 4000
      });
    } else {
      console.error('Error generating tags:', error);
      toastStore.dispatch('addMessage', {
        message: 'Failed to generate tags',
        icon: 'error',
        ttl: 3000
      });
    }
  } finally {
    isGeneratingTags.value = false;
  }
};

defineExpose({ open, close });
</script>
